import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Gavel, Clock, Users, Eye, Lock, ChevronRight, Play, 
  AlertTriangle, CheckCircle, Trophy, Zap, Timer, Tv
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUrl';
import { formatCountdown } from '../utils/timezone';

const AuctionHouse = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('live');
  const [isActivated, setIsActivated] = useState(false);
  const [activationChecked, setActivationChecked] = useState(false);

  useEffect(() => {
    checkActivation();
  }, [user]);

  useEffect(() => {
    fetchAuctions();
  }, [filter]);

  const checkActivation = async () => {
    if (user) {
      try {
        const response = await api.get('/auth/me');
        setIsActivated(response.data?.is_activated || false);
      } catch (error) {
        setIsActivated(false);
      }
    }
    setActivationChecked(true);
  };

  const fetchAuctions = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/auction/list?status=${filter}`);
      setAuctions(response.data);
    } catch (error) {
      console.error('Failed to fetch auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeRemaining = (endTime) => {
    const end = new Date(endTime);
    const now = new Date();
    const diff = end - now;
    
    if (diff <= 0) return 'Ended';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return 'bg-green-500';
      case 'upcoming': return 'bg-blue-500';
      case 'ended': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  // Show loading while checking activation
  if (!activationChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Show lock screen for non-logged in users only
  // Free members can view auctions (some auctions allow free entry)
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
        <div className="glass-card p-8 text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
            <Lock className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Auction House</h1>
          <h2 className="text-xl text-primary mb-4">Login Required</h2>
          <p className="text-muted-foreground mb-6">
            Login to view and participate in live auctions. 
            Some auctions are FREE for all members!
          </p>
          <div className="space-y-3">
            <Link
              to="/login"
              className="block w-full bg-primary text-primary-foreground py-3 rounded-xl font-semibold"
            >
              Login to Continue
            </Link>
            <Link
              to="/deals"
              className="block w-full bg-white/10 py-3 rounded-xl font-semibold"
            >
              Back to Marketplace
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <div className="relative bg-gradient-to-br from-accent/20 via-background to-primary/10 py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-accent/10 px-4 py-2 rounded-full text-accent text-sm font-medium mb-4">
            <Gavel className="w-4 h-4" />
            Auction House
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            ANANT <span className="text-accent">AUCTIONS</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-6">
            Bid on exclusive items! Real-time bidding with anti-snipe protection.
          </p>
          
          {/* Live Counter */}
          <div className="inline-flex items-center gap-4 bg-white/5 px-6 py-3 rounded-full">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-green-500 font-semibold">
                {auctions.filter(a => a.status === 'live').length} Live Auctions
              </span>
            </div>
            <div className="w-px h-6 bg-white/20"></div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {auctions.reduce((sum, a) => sum + (a.unique_bidders || 0), 0)} Active Bidders
              </span>
            </div>
            <div className="w-px h-6 bg-white/20"></div>
            <Link 
              to="/display/auctions"
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
              data-testid="display-tv-btn"
            >
              <Tv className="w-4 h-4" />
              <span className="font-semibold">Display TV</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-white/10 bg-card/50 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            {['live', 'upcoming', 'ended'].map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-5 py-2 rounded-full font-medium transition-all capitalize ${
                  filter === status
                    ? status === 'live' 
                      ? 'bg-green-500 text-white'
                      : status === 'upcoming'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-500 text-white'
                    : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                {status === 'live' && <span className="mr-2">●</span>}
                {status}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Auctions Grid */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : auctions.length === 0 ? (
          <div className="text-center py-20">
            <Gavel className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No {filter} auctions</h3>
            <p className="text-muted-foreground">
              {filter === 'live' 
                ? 'Check back soon for live auctions!'
                : filter === 'upcoming'
                ? 'No upcoming auctions scheduled yet.'
                : 'No auctions have ended recently.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {auctions.map(auction => (
              <Link
                key={auction.id}
                to={`/auction/${auction.id}`}
                className="group glass-card overflow-hidden hover:border-accent/50 transition-all"
              >
                {/* Image */}
                <div className="relative aspect-video bg-white/5">
                  {auction.images?.[0] ? (
                    <img
                      src={getImageUrl(auction.images[0])}
                      alt={auction.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Gavel className="w-12 h-12 text-muted-foreground" />
                    </div>
                  )}
                  
                  {/* Status Badge */}
                  <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(auction.status)}`}>
                    {auction.status === 'live' && <span className="mr-1">●</span>}
                    {auction.status.toUpperCase()}
                  </div>
                  
                  {/* Free Entry Badge */}
                  {auction.allow_free_members && (
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-semibold bg-green-500 text-white">
                      FREE ENTRY
                    </div>
                  )}
                  
                  {/* Timer */}
                  {auction.status === 'live' && (
                    <div className="absolute bottom-3 right-3 flex items-center gap-1 px-3 py-1.5 rounded-full bg-black/80 text-white text-sm font-mono">
                      <Timer className="w-4 h-4 text-red-500" />
                      {formatTimeRemaining(auction.end_time)}
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-2 line-clamp-1 group-hover:text-accent transition-colors">
                    {auction.title}
                  </h3>
                  
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Current Bid</p>
                      <p className="text-2xl font-bold text-accent">
                        ₹{auction.current_bid?.toLocaleString()}
                      </p>
                    </div>
                    
                    {auction.buy_now_price && auction.status === 'live' && (
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Buy Now</p>
                        <p className="text-lg font-semibold text-green-500">
                          ₹{auction.buy_now_price?.toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-white/10 text-sm text-muted-foreground">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {auction.unique_bidders || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Gavel className="w-4 h-4" />
                        {auction.total_bids || 0} bids
                      </span>
                    </div>
                    
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {auction.views || 0}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="max-w-6xl mx-auto px-4 pb-12">
        <div className="glass-card p-6">
          <h3 className="text-xl font-bold mb-4">Auction Rules</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Entry Pass Required</h4>
                <p className="text-sm text-muted-foreground">
                  Redeem a listing pass to enter each auction.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Binding Bids</h4>
                <p className="text-sm text-muted-foreground">
                  All bids are final. You cannot cancel once placed.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Timer className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Anti-Snipe</h4>
                <p className="text-sm text-muted-foreground">
                  Last 30 sec bids add 2 mins to timer.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Trophy className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Winner Pays</h4>
                <p className="text-sm text-muted-foreground">
                  24 hours to complete payment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuctionHouse;
